import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from '../../shared/types';
import { api } from '../lib/api';

export type DrawerType =
  | 'person'
  | 'client'
  | 'project'
  | 'contact'
  | 'profile'
  | 'invoice-view'
  | 'invoice-new';

export interface DrawerState {
  type: DrawerType;
  mode: 'create' | 'edit' | 'view';
  // id of the record being edited/viewed, or context (e.g. clientId for a new contact)
  id?: number;
  context?: Record<string, unknown>;
}

interface AppCtx {
  dark: boolean;
  toggleDark: () => void;
  collapsed: boolean;
  toggleCollapse: () => void;
  user: User | null;
  loadingUser: boolean;
  drawer: DrawerState | null;
  openDrawer: (d: DrawerState) => void;
  closeDrawer: () => void;
  dataVersion: number;
  refresh: () => void;
}

const Ctx = createContext<AppCtx | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState(() => localStorage.getItem('cc-theme') === 'dark');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('cc-collapsed') === '1');
  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    document.documentElement.classList.toggle('cc-theme-dark', dark);
    document.documentElement.classList.toggle('cc-theme-light', !dark);
    localStorage.setItem('cc-theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    localStorage.setItem('cc-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    api
      .get<User>('/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoadingUser(false));
  }, []);

  const refresh = useCallback(() => setDataVersion((v) => v + 1), []);

  const value = useMemo<AppCtx>(
    () => ({
      dark,
      toggleDark: () => setDark((d) => !d),
      collapsed,
      toggleCollapse: () => setCollapsed((c) => !c),
      user,
      loadingUser,
      drawer,
      openDrawer: setDrawer,
      closeDrawer: () => setDrawer(null),
      dataVersion,
      refresh,
    }),
    [dark, collapsed, user, loadingUser, drawer, dataVersion, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApp must be used within AppProvider');
  return c;
}

// Small data-fetching hook tied to the global dataVersion so mutations refresh.
export function useFetch<T>(path: string | null): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const { dataVersion } = useApp();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localV, setLocalV] = useState(0);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    api
      .get<T>(path)
      .then((d) => live && (setData(d), setError(null)))
      .catch((e) => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [path, dataVersion, localV]);

  return { data, loading, error, reload: () => setLocalV((v) => v + 1) };
}
